import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import { Chart, registerables } from "chart.js";
import { Subscription } from "rxjs";

interface ProtocolDeviceRow {
  deviceId: string;
  deviceSn: string;
  vin: string;
  protocol: string;
  vehicleType: string;
  lastContact: string;
  status: "Active" | "Idle";
}

interface ProtocolSlice {
  id: string;
  label: string;
  pct: number;
  count: number;
  color: string;
}

interface ProtocolReportData {
  totalDevices: number;
  slices: ProtocolSlice[];
  devices: ProtocolDeviceRow[];
}

@Component({
  selector: "app-protocol-report",
  templateUrl: "./protocol-report.component.html",
  styleUrls: ["./protocol-report.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ProtocolReportComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly canvasId = "protocolReportDonutChart";
  private donutChart: any = null;
  private readonly subscriptions = new Subscription();

  readonly fleetControl = new UntypedFormControl("Metro Fleet");
  readonly fleetOptions = [
    "Metro Fleet",
    "Regional Fleet",
    "National Fleet",
  ];

  readonly productCodeControl = new UntypedFormControl("DL972");
  readonly productCodeOptions = ["DL972", "DL800", "DL650"];

  deviceSearch = "";
  currentPage = 1;
  readonly pageSize = 5;

  private readonly dataByProduct: Record<string, ProtocolReportData> = {
    DL972: {
      totalDevices: 1527,
      slices: [
        {
          id: "j1939",
          label: "J1939",
          pct: 55,
          count: 840,
          color: "#10B77F",
        },
        {
          id: "obd",
          label: "OBD",
          pct: 25,
          count: 382,
          color: "#4E8DDE",
        },
        {
          id: "j1708",
          label: "J1708",
          pct: 15,
          count: 229,
          color: "#F59F0A",
        },
        {
          id: "other",
          label: "Other",
          pct: 5,
          count: 76,
          color: "#94A3B8",
        },
      ],
      devices: [
        {
          deviceId: "DL1234",
          deviceSn: "SN-981723",
          vin: "1HGCM82633A123456",
          protocol: "J1939",
          vehicleType: "Truck",
          lastContact: "10 mins ago",
          status: "Active",
        },
        {
          deviceId: "DL4277",
          deviceSn: "SN-981724",
          vin: "1FTFW1ET5EKD12345",
          protocol: "J1939",
          vehicleType: "Truck",
          lastContact: "1 hour ago",
          status: "Active",
        },
        {
          deviceId: "DL1345",
          deviceSn: "SN-871205",
          vin: "2C4RC1BG7HR654321",
          protocol: "OBD",
          vehicleType: "Car",
          lastContact: "3 hours ago",
          status: "Active",
        },
        {
          deviceId: "DL3545",
          deviceSn: "SN-772809",
          vin: "3FA6P0H74JR210987",
          protocol: "J1708",
          vehicleType: "Bus",
          lastContact: "5 hours ago",
          status: "Active",
        },
        {
          deviceId: "DL4251",
          deviceSn: "SN-664521",
          vin: "1N4AL3AP4JC345678",
          protocol: "J1939",
          vehicleType: "Truck",
          lastContact: "3 days ago",
          status: "Idle",
        },
        {
          deviceId: "DL9012",
          deviceSn: "SN-510004",
          vin: "5NPE24AF8FH789012",
          protocol: "OBD",
          vehicleType: "Van",
          lastContact: "12 hours ago",
          status: "Active",
        },
        {
          deviceId: "DL7788",
          deviceSn: "SN-739115",
          vin: "1GNEK13ZX3R146852",
          protocol: "J1939",
          vehicleType: "Truck",
          lastContact: "1 day ago",
          status: "Active",
        },
        {
          deviceId: "DL2210",
          deviceSn: "SN-602310",
          vin: "2G1WF52E659367214",
          protocol: "Other",
          vehicleType: "Truck",
          lastContact: "2 days ago",
          status: "Idle",
        },
        {
          deviceId: "DL6654",
          deviceSn: "SN-881004",
          vin: "1FDXF46S12EC77881",
          protocol: "J1708",
          vehicleType: "Bus",
          lastContact: "30 mins ago",
          status: "Active",
        },
        {
          deviceId: "DL3300",
          deviceSn: "SN-743920",
          vin: "1C4RJFAG5FC901245",
          protocol: "J1939",
          vehicleType: "Truck",
          lastContact: "4 hours ago",
          status: "Active",
        },
        {
          deviceId: "DL4411",
          deviceSn: "SN-520711",
          vin: "WAUZZZ8K9DA145672",
          protocol: "OBD",
          vehicleType: "Car",
          lastContact: "6 hours ago",
          status: "Active",
        },
        {
          deviceId: "DL5522",
          deviceSn: "SN-938401",
          vin: "JHMCM56557C404112",
          protocol: "J1939",
          vehicleType: "Truck",
          lastContact: "2 hours ago",
          status: "Active",
        },
      ],
    },
    DL800: {
      totalDevices: 1012,
      slices: [
        {
          id: "obd",
          label: "OBD",
          pct: 42,
          count: 425,
          color: "#4E8DDE",
        },
        {
          id: "j1939",
          label: "J1939",
          pct: 31,
          count: 313,
          color: "#10B77F",
        },
        {
          id: "j1708",
          label: "J1708",
          pct: 18,
          count: 182,
          color: "#F59F0A",
        },
        {
          id: "other",
          label: "Other",
          pct: 9,
          count: 92,
          color: "#94A3B8",
        },
      ],
      devices: [
        {
          deviceId: "DL800-1001",
          deviceSn: "SN-800101",
          vin: "1M8GDM9AXKP042788",
          protocol: "OBD",
          vehicleType: "Van",
          lastContact: "15 mins ago",
          status: "Active",
        },
        {
          deviceId: "DL800-1002",
          deviceSn: "SN-800102",
          vin: "2HKRL18651H509321",
          protocol: "J1939",
          vehicleType: "Truck",
          lastContact: "35 mins ago",
          status: "Active",
        },
        {
          deviceId: "DL800-1003",
          deviceSn: "SN-800103",
          vin: "3N1BC13E79L435118",
          protocol: "OBD",
          vehicleType: "Car",
          lastContact: "1 hour ago",
          status: "Active",
        },
        {
          deviceId: "DL800-1004",
          deviceSn: "SN-800104",
          vin: "1G8ZH52821Z123640",
          protocol: "J1708",
          vehicleType: "Bus",
          lastContact: "2 hours ago",
          status: "Idle",
        },
        {
          deviceId: "DL800-1005",
          deviceSn: "SN-800105",
          vin: "2FMZA51431BA27842",
          protocol: "OBD",
          vehicleType: "Van",
          lastContact: "5 hours ago",
          status: "Active",
        },
        {
          deviceId: "DL800-1006",
          deviceSn: "SN-800106",
          vin: "5TDZT34A54S218903",
          protocol: "J1939",
          vehicleType: "Truck",
          lastContact: "8 hours ago",
          status: "Active",
        },
        {
          deviceId: "DL800-1007",
          deviceSn: "SN-800107",
          vin: "1FAHP2E88FG141276",
          protocol: "Other",
          vehicleType: "Car",
          lastContact: "1 day ago",
          status: "Idle",
        },
      ],
    },
    DL650: {
      totalDevices: 768,
      slices: [
        {
          id: "j1939",
          label: "J1939",
          pct: 48,
          count: 369,
          color: "#10B77F",
        },
        {
          id: "j1708",
          label: "J1708",
          pct: 27,
          count: 207,
          color: "#F59F0A",
        },
        {
          id: "obd",
          label: "OBD",
          pct: 16,
          count: 123,
          color: "#4E8DDE",
        },
        {
          id: "other",
          label: "Other",
          pct: 9,
          count: 69,
          color: "#94A3B8",
        },
      ],
      devices: [
        {
          deviceId: "DL650-2201",
          deviceSn: "SN-650201",
          vin: "1N6AD0EV5KN782401",
          protocol: "J1939",
          vehicleType: "Truck",
          lastContact: "9 mins ago",
          status: "Active",
        },
        {
          deviceId: "DL650-2202",
          deviceSn: "SN-650202",
          vin: "JF1GPAL68CH215009",
          protocol: "J1708",
          vehicleType: "Bus",
          lastContact: "45 mins ago",
          status: "Active",
        },
        {
          deviceId: "DL650-2203",
          deviceSn: "SN-650203",
          vin: "1C6RR7TM8GS268903",
          protocol: "J1939",
          vehicleType: "Truck",
          lastContact: "2 hours ago",
          status: "Active",
        },
        {
          deviceId: "DL650-2204",
          deviceSn: "SN-650204",
          vin: "4S4BP61C237356200",
          protocol: "OBD",
          vehicleType: "Car",
          lastContact: "4 hours ago",
          status: "Idle",
        },
        {
          deviceId: "DL650-2205",
          deviceSn: "SN-650205",
          vin: "1J4GZ78Y8VC651204",
          protocol: "J1708",
          vehicleType: "Bus",
          lastContact: "10 hours ago",
          status: "Active",
        },
        {
          deviceId: "DL650-2206",
          deviceSn: "SN-650206",
          vin: "3VWFE21C04M000672",
          protocol: "Other",
          vehicleType: "Van",
          lastContact: "2 days ago",
          status: "Idle",
        },
      ],
    },
  };

  get totalDevices(): number {
    return this.selectedData.totalDevices;
  }

  get protocolSlices(): ProtocolSlice[] {
    return this.selectedData.slices;
  }

  ngOnInit(): void {
    Chart.register(...registerables);
    this.subscriptions.add(
      this.productCodeControl.valueChanges.subscribe(() => {
        this.currentPage = 1;
        this.renderDonut();
      })
    );
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => this.renderDonut());
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.donutChart) {
      this.donutChart.destroy();
      this.donutChart = null;
    }
  }

  get filteredDevices(): ProtocolDeviceRow[] {
    const q = this.deviceSearch.trim().toLowerCase();
    const allDevices = this.selectedData.devices;
    if (!q) {
      return [...allDevices];
    }
    return allDevices.filter(
      (r) =>
        r.deviceId.toLowerCase().includes(q) ||
        r.deviceSn.toLowerCase().includes(q) ||
        r.vin.toLowerCase().includes(q) ||
        r.protocol.toLowerCase().includes(q) ||
        r.vehicleType.toLowerCase().includes(q) ||
        r.lastContact.toLowerCase().includes(q) ||
        r.status.toLowerCase().includes(q)
    );
  }

  get pageCount(): number {
    return Math.max(1, Math.ceil(this.filteredDevices.length / this.pageSize));
  }

  get pagedDevices(): ProtocolDeviceRow[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredDevices.slice(start, start + this.pageSize);
  }

  get globalRowOffset(): number {
    return (this.currentPage - 1) * this.pageSize;
  }

  onSearchInput(): void {
    this.currentPage = 1;
  }

  goPage(page: number): void {
    const p = Math.min(Math.max(1, page), this.pageCount);
    this.currentPage = p;
  }

  goFirst(): void {
    this.goPage(1);
  }

  goPrev(): void {
    this.goPage(this.currentPage - 1);
  }

  goNext(): void {
    this.goPage(this.currentPage + 1);
  }

  goLast(): void {
    this.goPage(this.pageCount);
  }

  onCalendarClick(): void {
    // Mock: wire to date picker when API is available
  }

  onFilterClick(): void {
    // Mock
  }

  onExportClick(): void {
    // Mock
  }

  trackDevice(_i: number, row: ProtocolDeviceRow): string {
    return row.deviceId + row.lastContact;
  }

  private renderDonut(): void {
    if (this.donutChart) {
      this.donutChart.destroy();
      this.donutChart = null;
    }
    const canvas = document.getElementById(
      this.canvasId
    ) as HTMLCanvasElement | null;
    if (!canvas) {
      return;
    }
    const slices = this.protocolSlices;
    this.donutChart = new Chart(canvas, {
      type: "doughnut",
      data: {
        labels: slices.map((s) => s.label),
        datasets: [
          {
            data: slices.map((s) => s.count),
            backgroundColor: slices.map((s) => s.color),
            borderColor: "#fff",
            borderWidth: 2,
            hoverOffset: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "68%",
        rotation: -90,
        circumference: 360,
        layout: { padding: 8 },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (item) => {
                const raw = Number(item.raw);
                const pct = this.totalDevices
                  ? Math.round((raw / this.totalDevices) * 100)
                  : 0;
                return `${item.label ?? ""}: ${raw} (${pct}%)`;
              },
            },
          },
        },
      },
    });
  }

  private get selectedData(): ProtocolReportData {
    const code = String(this.productCodeControl.value ?? "").trim().toUpperCase();
    return this.dataByProduct[code] ?? this.dataByProduct[this.productCodeOptions[0]];
  }
}
